import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, Navigate, Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Layers3,
  LayoutTemplate,
  Globe,
  Eye,
  PenSquare,
  Palette,
  Plug,
  Sparkles,
  Users,
  Building2,
  UserPlus,
  Link,
  Rocket,
  User,
  CreditCard,
  Settings,
  HelpCircle,
  ChevronDown,
  LogOut,
  X,
  Menu,
  ChevronsLeft,
  ChevronsRight,
  Compass,
  FileText,
  UserCircle,
  Bot,
  ChevronRight,
  type LucideIcon,
  Home,
  Inbox,
  Briefcase,
  Loader2,
  MessageSquarePlus,
  PanelBottom,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store/authStore';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { UpgradePrompt } from '@/components/intake/UpgradePrompt';
import { AiBudgetBanner } from '@/components/AiBudgetBanner';
import { PaymentPromptBanner } from '@/components/billing/PaymentPromptBanner';
import { ClaimAccountBanner } from '@/components/ClaimAccountBanner';
import { runTour } from '@/lib/tour/runTour';
import { matchPageTour } from '@/lib/tour/pageTours';
import { hasSeenDashboardTour, markDashboardTourSeen } from '@/lib/tour/tourState';
import { planShortLabel } from '@/lib/plans';
import { useLimitedAccessState, type LimitedAccessState } from '@/hooks/useLimitedAccess';
import { limitedHomePath, pageAllowed, pageArea, pathAllowed, type LimitedAccess } from '@/lib/limitedAccess';
import { LimitedSiteBar } from '@/components/limited/LimitedSiteBar';
import { ClosedPage } from '@/components/limited/ClosedPage';

const BRAND_NAME = import.meta.env.VITE_BRAND_NAME || 'Bread List Sites';
const SIDEBAR_COLLAPSED_KEY = 'bls_sidebarCollapsed';

const PLAN_COLORS: Record<string, string> = {
  free: 'bg-brand-lilac-2 text-brand-ink',
  starter: 'bg-brand-lilac text-brand-plum',
  pro: 'bg-brand-lavender text-brand-midnight',
  agency: 'bg-brand-plum text-brand-lilac',
};

// Playful color-pop chips: each nav icon sits in a soft tinted square so the plum
// sidebar reads as lively + friendly instead of a monotone purple slab. Idle = soft
// translucent tint on the dark bg; active = solid chip (the item's pill lights up too).
type NavTint = 'lavender' | 'teal' | 'sand' | 'lilac';
const NAV_TINTS: Record<NavTint, { idle: string; active: string }> = {
  lavender: { idle: 'bg-brand-lavender/15 text-brand-lavender', active: 'bg-brand-lavender text-brand-midnight' },
  teal: { idle: 'bg-brand-teal/25 text-brand-teal', active: 'bg-brand-teal text-brand-midnight' },
  sand: { idle: 'bg-brand-sand/20 text-brand-sand', active: 'bg-brand-sand text-brand-midnight' },
  lilac: { idle: 'bg-brand-lilac/15 text-brand-lilac', active: 'bg-brand-lilac text-brand-plum-deep' },
};

type NavLeaf = { to: string; label: string; icon: LucideIcon; tourId?: string; tint?: NavTint };
// A group with a `to` gets a clickable header (navigates to that page) + a chevron to expand;
// without `to` the header is a plain expand/collapse toggle (e.g. "Website Content").
type NavGroup = { label: string; icon: LucideIcon; to?: string; tourId?: string; children: NavLeaf[]; tint?: NavTint };
type NavEntry = NavLeaf | NavGroup;
const isNavGroup = (entry: NavEntry): entry is NavGroup => 'children' in entry;

// A rounded color chip around a nav icon.
function NavIconChip({ icon: Icon, tint = 'lavender', active, nested }: { icon: LucideIcon; tint?: NavTint; active: boolean; nested?: boolean }) {
  const t = NAV_TINTS[tint];
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center rounded-lg transition duration-200 group-hover:scale-110',
        nested ? 'h-6 w-6' : 'h-7 w-7',
        // Active row pill is light lavender → a dark chip reads cleanly on it; idle rows
        // get the soft color-pop tint on the dark plum sidebar.
        active ? 'bg-brand-midnight text-brand-lavender' : t.idle,
      )}
    >
      <Icon className={nested ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
    </span>
  );
}

// Nav is organized into labeled sections so the sidebar reads as a structured
// workspace (quiet eyebrow per zone) rather than one long undifferentiated list.
// The collapsible groups keep their behavior; sections just add visual rhythm.
type NavSection = { heading?: string; items: NavEntry[] };

const navSections: NavSection[] = [
  {
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, tint: 'lavender' },
      { to: '/sites', label: 'My Sites', icon: Layers3, tourId: 'sites', tint: 'teal' },
      { to: '/studio', label: 'My Website', icon: Globe, tourId: 'studio', tint: 'sand' },
      // The Studio in preview mode: the same draft the Studio edits, before Publish, with the
      // editing panels out of the way. Not the live site (the old Preview page iframed that).
      { to: '/studio?view=preview', label: 'Preview', icon: Eye, tint: 'lilac' },
    ],
  },
  {
    heading: 'Design',
    items: [
      { to: '/looks', label: 'Pick a Look', icon: LayoutTemplate, tourId: 'design', tint: 'lilac' },
      { to: '/cards', label: 'Card Style', icon: CreditCard, tint: 'teal' },
    ],
  },
  {
    heading: 'Manage',
    items: [
      {
        label: 'Website Content',
        icon: FileText,
        tourId: 'content',
        tint: 'sand',
        children: [
          { to: '/brands', label: 'Brand Manager', icon: Palette, tourId: 'brand', tint: 'lavender' },
          { to: '/integrations', label: 'Integrations', icon: Plug, tourId: 'integrations', tint: 'teal' },
          { to: '/studio/blog', label: 'Blog Posts', icon: PenSquare, tourId: 'blog', tint: 'sand' },
          { to: '/studio/communities', label: 'Communities', icon: Users, tourId: 'communities', tint: 'lilac' },
          { to: '/offices', label: 'Offices', icon: Building2, tint: 'teal' },
          { to: '/team?view=agents', label: 'Agents', icon: UserCircle, tint: 'lavender' },
          { to: '/listing-entry', label: 'List a Home', icon: Home, tint: 'teal' },
        ],
      },
      { to: '/ai-marketer', label: 'AI Marketer', icon: Sparkles, tourId: 'marketer', tint: 'sand' },
      { to: '/autopilot', label: 'SEO Autopilot', icon: Bot, tourId: 'autopilot', tint: 'lilac' },
      {
        label: 'Go Live',
        icon: Rocket,
        to: '/go-live',
        tourId: 'golive',
        tint: 'teal',
        children: [
          { to: '/domains', label: 'Domains', icon: Link, tint: 'lavender' },
        ],
      },
      {
        label: 'My Account',
        icon: User,
        to: '/account',
        tourId: 'account',
        tint: 'lavender',
        children: [
          { to: '/team', label: 'Team', icon: UserPlus, tint: 'teal' },
          { to: '/ai-usage', label: 'AI Usage', icon: Sparkles, tint: 'lilac' },
          { to: '/billing', label: 'Billing', icon: CreditCard, tint: 'sand' },
          { to: '/settings', label: 'Settings', icon: Settings, tint: 'lavender' },
        ],
      },
    ],
  },
];

/**
 * The sidebar for a limited person (lib/limitedAccess.ts): only the screens they may use, plus the
 * ones that are normally reached from elsewhere — Leads, Business info and Footer links. Not limited: unchanged.
 */
export function navSectionsFor(access: LimitedAccess): NavSection[] {
  if (!access.limited) return navSections;
  const extras: NavLeaf[] = [
    { to: '/leads', label: 'Leads', icon: Inbox, tint: 'teal' },
    { to: '/business-info', label: 'Business info', icon: Briefcase, tint: 'sand' },
    { to: '/footer-links', label: 'Footer links', icon: PanelBottom, tint: 'lavender' },
    { to: '/requests', label: 'Request a change', icon: MessageSquarePlus, tint: 'lilac' },
  ];
  const keep = (entry: NavEntry): NavEntry | null => {
    if (!isNavGroup(entry)) return pathAllowed(access, entry.to) ? entry : null;
    const children = entry.children.filter((c) => pathAllowed(access, c.to));
    const to = entry.to && pathAllowed(access, entry.to) ? entry.to : undefined;
    if (!to && children.length === 0) return null;
    return { ...entry, to, children };
  };
  return navSections
    .map((section) => {
      // The extras go right after Website Content, before the filter drops anything.
      const all = section.heading === 'Manage'
        ? section.items.flatMap((e) => (isNavGroup(e) && e.label === 'Website Content' ? [e, ...extras] : [e]))
        : section.items;
      const items = all.map(keep).filter((e): e is NavEntry => e !== null);
      return { ...section, items };
    })
    .filter((section) => section.items.length > 0);
}

/** The page, or why a limited person can't use it (the gateway refuses either way). */
function LimitedOutlet({ state }: { state: LimitedAccessState }) {
  const location = useLocation();
  const { access } = state;
  // Another site was picked and its answer is not in: hold the page, never guess from the primary.
  if (state.pending) return <AccessPending failed={state.failed} retry={state.retry} />;
  if (!access.limited) return <Outlet />;
  const area = pageArea(location.pathname, location.search);
  if (pageAllowed(access, area)) return <Outlet />;
  // A Contributor's home is their own area: a Leads-only person opens on the Leads inbox.
  if (area === 'dashboard') return <Navigate to={limitedHomePath(access)} replace />;
  return <ClosedPage access={access} area={area} />;
}

function AccessPending({ failed, retry }: { failed: boolean; retry: () => void }) {
  if (failed) {
    return (
      <div className="mx-auto max-w-lg p-6 text-center" data-access-pending="failed">
        <p className="text-sm text-brand-ink-soft">We couldn't check what you can change on this site.</p>
        <Button className="mt-4 bg-brand-plum text-white hover:bg-brand-plum-deep" onClick={retry}>Try again</Button>
      </div>
    );
  }
  return (
    <div className="flex justify-center p-10" data-access-pending role="status" aria-label="Checking your access">
      <Loader2 className="h-6 w-6 animate-spin text-brand-plum" />
    </div>
  );
}

function routeIsActive(pathname: string, search: string, to: string): boolean {
  const target = new URL(to, 'https://builder.local');
  if (pathname !== target.pathname) return false;

  const current = new URLSearchParams(search);

  // /builder surfaces (existing behavior)
  if (target.pathname === '/builder') {
    const t = target.searchParams.get('surface');
    const c = current.get('surface');
    return t ? c === t : !c;
  }

  // /studio and /studio?view=preview share one path: "My Website" vs "Preview".
  if (target.pathname === '/studio') {
    const previewing = current.get('view') === 'preview';
    return target.searchParams.get('view') === 'preview' ? previewing : !previewing;
  }

  // /team has two views that share one path: the default users/staff view and ?view=agents.
  // Distinguish them so "Team" and "Agents" don't both highlight at once.
  if (target.pathname === '/team') {
    const t = target.searchParams.get('view');
    const c = current.get('view');
    return t ? c === t : c !== 'agents';
  }

  return true;
}

// Persist whether each nav group is expanded (defaults to open), keyed by group label.
function navGroupOpenKey(label: string): string {
  return 'bls_navOpen_' + label.toLowerCase().replace(/[^a-z0-9]+/g, '_');
}
function readNavGroupOpen(label: string): boolean {
  try {
    const v = localStorage.getItem(navGroupOpenKey(label));
    return v === null ? true : v === '1';
  } catch {
    return true;
  }
}
function writeNavGroupOpen(label: string, open: boolean): void {
  try {
    localStorage.setItem(navGroupOpenKey(label), open ? '1' : '0');
  } catch {
    /* ignore */
  }
}

const NAV_LINK_BASE =
  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors';

function NavLeafLink({
  leaf,
  collapsed,
  nested = false,
  onClose,
}: {
  leaf: NavLeaf;
  collapsed: boolean;
  nested?: boolean;
  onClose?: () => void;
}) {
  const location = useLocation();
  const Icon = leaf.icon;
  const isCurrent = routeIsActive(location.pathname, location.search, leaf.to);
  return (
    <NavLink
      to={leaf.to}
      data-tour={leaf.tourId}
      onClick={onClose}
      // Native tooltip so the icon-only rail is still discoverable.
      title={collapsed ? leaf.label : undefined}
      className={cn(
        NAV_LINK_BASE,
        'group gap-2.5',
        nested && !collapsed && 'pl-5',
        collapsed && 'lg:justify-center lg:px-2',
        isCurrent
          ? 'bg-brand-lavender text-brand-midnight shadow-[2px_2px_0_0_#0D0220] ring-1 ring-white/15'
          : 'text-brand-lavender/70 hover:bg-white/[0.06] hover:text-white',
      )}
    >
      <NavIconChip icon={Icon} tint={leaf.tint} active={isCurrent} nested={nested} />
      <span className={cn('truncate', collapsed && 'lg:hidden')}>{leaf.label}</span>
    </NavLink>
  );
}

function NavGroupBlock({
  group,
  collapsed,
  onClose,
}: {
  group: NavGroup;
  collapsed: boolean;
  onClose?: () => void;
}) {
  const location = useLocation();
  const Icon = group.icon;
  const headerActive = group.to
    ? routeIsActive(location.pathname, location.search, group.to)
    : false;
  const childActive = group.children.some((c) =>
    routeIsActive(location.pathname, location.search, c.to),
  );
  const [open, setOpen] = useState(() => readNavGroupOpen(group.label));
  const toggle = () =>
    setOpen((o) => {
      const next = !o;
      writeNavGroupOpen(group.label, next);
      return next;
    });
  const chevron = (
    <ChevronRight className={cn('h-4 w-4 shrink-0 transition-transform', open && 'rotate-90')} />
  );

  return (
    <div>
      {/* Header. With a `to` it's a clickable link to the group's page + a chevron to expand;
          without one it's a plain expand/collapse toggle. Hidden on the desktop icon rail
          (lg-collapsed), where the children render as flat icons so they stay reachable. */}
      {group.to ? (
        <div
          className={cn(
            'group/navgroup flex items-center rounded-md',
            headerActive ? 'bg-brand-lavender shadow-[2px_2px_0_0_#0D0220] ring-1 ring-white/15' : 'hover:bg-white/[0.06]',
            collapsed && 'lg:justify-center',
          )}
        >
          <NavLink
            to={group.to}
            data-tour={group.tourId}
            onClick={onClose}
            title={collapsed ? group.label : undefined}
            className={cn(
              'flex flex-1 items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              collapsed && 'lg:justify-center lg:px-2',
              headerActive
                ? 'text-brand-midnight'
                : 'text-brand-lavender/70 group-hover/navgroup:text-brand-lavender',
            )}
          >
            <NavIconChip icon={Icon} tint={group.tint} active={headerActive} />
            <span className={cn('truncate', collapsed && 'lg:hidden')}>{group.label}</span>
          </NavLink>
          <button
            type="button"
            onClick={toggle}
            aria-expanded={open}
            aria-label={`${open ? 'Collapse' : 'Expand'} ${group.label}`}
            className={cn(
              'shrink-0 rounded-md px-2 py-2 transition-colors group-hover/navgroup:text-brand-lavender',
              headerActive ? 'text-brand-plum' : 'text-brand-lavender/60',
              collapsed && 'lg:hidden',
            )}
          >
            {chevron}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={toggle}
          data-tour={group.tourId}
          aria-expanded={open}
          title={collapsed ? group.label : undefined}
          className={cn(
            NAV_LINK_BASE,
            'w-full gap-2.5',
            collapsed && 'lg:hidden',
            childActive
              ? 'text-brand-lavender'
              : 'text-brand-lavender/70 hover:bg-white/[0.06] hover:text-white',
          )}
        >
          <NavIconChip icon={Icon} tint={group.tint} active={childActive} />
          <span className="flex-1 truncate text-left">{group.label}</span>
          {chevron}
        </button>
      )}
      {/* Children: shown when open. On the icon rail they stay visible even when closed
          (collapsed && lg:block) so grouped items remain reachable without expanding the sidebar. */}
      <div className={cn('mt-1 space-y-1', open ? 'block' : 'hidden', collapsed && 'lg:block')}>
        {group.children.map((child) => (
          <NavLeafLink key={child.to} leaf={child} collapsed={collapsed} nested onClose={onClose} />
        ))}
      </div>
    </div>
  );
}

// `collapsed` shrinks the sidebar to an icon rail on desktop (lg+). All the
// collapse styling is `lg:`-scoped, so the mobile slide-in overlay always shows
// the full labelled sidebar regardless of the persisted collapsed state.
function AgentSidebar({ collapsed = false, onClose, access }: { collapsed?: boolean; onClose?: () => void; access: LimitedAccess }) {
  const sections = navSectionsFor(access);
  return (
    <div
      className="sidebar-tex flex h-full flex-col"
      style={{ background: 'linear-gradient(176deg, #10032A 0%, #190733 58%, #0D0220 100%)' }}
    >
      {/* Header */}
      <div
        className="flex h-14 items-center justify-between px-4"
        style={{ borderBottom: '1px solid rgba(201,184,244,0.12)' }}
      >
        <div className={cn('flex items-center gap-2.5', collapsed && 'lg:w-full lg:justify-center')}>
          <div
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-bold ring-1 ring-white/10"
            style={{
              background: 'linear-gradient(135deg, #C9B8F4 0%, #E6D9FC 100%)',
              color: '#10032A',
              fontFamily: 'Montserrat, sans-serif',
              boxShadow: '0 2px 10px rgba(201,184,244,0.35)',
            }}
          >
            BL
          </div>
          <span
            className={cn('font-semibold tracking-tight text-white', collapsed && 'lg:hidden')}
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            {BRAND_NAME}
          </span>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="rounded-md p-1 lg:hidden text-brand-lavender/60 hover:text-brand-lavender"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className={cn('flex-1 space-y-5 overflow-y-auto py-5', collapsed ? 'px-3 lg:px-2' : 'px-3')} data-tour="sidebar">
        {sections.map((section, i) => (
          <div key={section.heading ?? `section-${i}`}>
            {section.heading && (
              collapsed ? (
                // On the icon rail the label can't fit — a hairline keeps the rhythm.
                <div className="mx-2 mb-2 hidden border-t border-white/10 lg:block" />
              ) : (
                <p
                  className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-brand-lavender/45"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {section.heading}
                </p>
              )
            )}
            <div className="space-y-1">
              {section.items.map((entry) =>
                isNavGroup(entry) ? (
                  <NavGroupBlock key={entry.label} group={entry} collapsed={collapsed} onClose={onClose} />
                ) : (
                  <NavLeafLink key={entry.to} leaf={entry} collapsed={collapsed} onClose={onClose} />
                ),
              )}
            </div>
          </div>
        ))}
      </nav>

      {/* Upgrade prompt — hidden on the collapsed rail (desktop) */}
      <div
        style={{ background: '#1a0a35', borderTop: '1px solid rgba(201,184,244,0.1)' }}
        className={cn((collapsed || access.limited) && 'lg:hidden', access.limited && 'hidden')}
      >
        {/* "Have us finish your site" means nothing on a site Bread List manages. */}
        {!access.limited && <UpgradePrompt />}
      </div>
    </div>
  );
}

function AgentTopBar({
  onMenuClick,
  onStartTour,
  hasTour,
  tourLabel,
  collapsed,
  onToggleCollapse,
}: {
  onMenuClick: () => void;
  onStartTour: () => void;
  hasTour: boolean;
  tourLabel: string;
  collapsed: boolean;
  onToggleCollapse: () => void;
}) {
  const { email, plan } = useAuthStore();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const firstName = email ? email.split('@')[0].split('.')[0] : '';
  const displayName = firstName.charAt(0).toUpperCase() + firstName.slice(1);
  const planColor = PLAN_COLORS[plan ?? 'free'];

  return (
    <header className="sticky top-0 z-10 flex h-14 items-center border-b bg-white px-4 shadow-sm">
      {/* Mobile: open the slide-in sidebar */}
      <button
        onClick={onMenuClick}
        className="mr-3 rounded-md p-1.5 text-brand-ink hover:bg-brand-surface lg:hidden"
        aria-label="Open menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Desktop: collapse / expand the sidebar to free up screen room */}
      <button
        onClick={onToggleCollapse}
        className="mr-3 hidden rounded-md p-1.5 text-brand-ink hover:bg-brand-surface lg:inline-flex"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronsRight className="h-5 w-5" /> : <ChevronsLeft className="h-5 w-5" />}
      </button>

      {/* Brand */}
      <div className="flex items-center gap-2">
        <span className="hidden font-heading font-semibold text-brand-midnight sm:block">{BRAND_NAME}</span>
      </div>

      <div className="flex-1" />

      <div className="flex items-center gap-3">
        {/* Plan badge */}
        {plan && (
          <span className={`hidden rounded-full px-2.5 py-0.5 text-xs font-medium sm:inline-flex ${planColor}`}>
            {planShortLabel(plan)}
          </span>
        )}

        {/* Tour button — the dashboard walkthrough ("Take a tour") or a per-page "Tour this page".
            Button-only and shown only on routes that have a tour registered; never auto-starts. */}
        {hasTour && (
          <button
            type="button"
            onClick={onStartTour}
            data-tour="tour-trigger"
            className="flex items-center gap-1 rounded-md px-3 py-1.5 text-sm text-brand-ink hover:bg-brand-surface hover:text-brand-plum"
            aria-label={tourLabel}
          >
            <Compass className="h-4 w-4" />
            <span className="hidden sm:inline">{tourLabel}</span>
          </button>
        )}

        {/* Help button */}
        {/* Help center — carries the current page so Help can open with that page's article. */}
        <RouterLink
          to={location.pathname.startsWith('/help') ? '/help' : `/help?from=${encodeURIComponent(location.pathname)}`}
          className="flex items-center gap-1 rounded-md px-3 py-1.5 text-sm text-brand-ink hover:bg-brand-surface hover:text-brand-plum"
          aria-label="Help"
          data-tour="help"
        >
          <HelpCircle className="h-4 w-4" />
          <span className="hidden sm:inline">Help</span>
        </RouterLink>

        {/* User menu — first name only */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-lilac text-brand-plum text-sm font-medium">
                {displayName ? displayName[0].toUpperCase() : 'A'}
              </div>
              <span className="hidden text-sm sm:block">{displayName}</span>
              <ChevronDown className="h-4 w-4 text-brand-ink" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => navigate('/profile')}>
              <User className="mr-2 h-4 w-4" />
              My Account
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="text-brand-error">
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

export function AgentLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Desktop sidebar collapse (icon rail). Persisted so it sticks across reloads.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const toggleCollapsed = () =>
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });

  // Guided tours. Every route with a registered tour gets a button-only "Tour this page"
  // walkthrough; the dashboard's also auto-starts once on the first visit (below).
  const location = useLocation();
  const accessState = useLimitedAccessState();
  const access = accessState.access;
  // The page tours point at sidebar entries a limited person doesn't have.
  const pageTour = access.limited ? null : matchPageTour(location.pathname);
  const startPageTour = useCallback(() => {
    if (!pageTour) return;
    // The dashboard tour targets the sidebar → open it first on mobile so the anchors exist.
    if (pageTour.needsSidebar && typeof window !== 'undefined' && window.innerWidth < 1024) {
      setSidebarOpen(true);
    }
    runTour(pageTour.getSteps());
  }, [pageTour]);

  // Auto-start the DASHBOARD tour ONCE — only the first time a user lands on /dashboard, and only
  // on desktop (where the sidebar, and thus every tour target, is always visible). Marked seen
  // immediately so it never auto-starts again; the per-page "Tour this page" walkthroughs are
  // button-only and NEVER auto-start.
  useEffect(() => {
    if (
      location.pathname === '/dashboard' &&
      !access.limited &&
      !hasSeenDashboardTour() &&
      typeof window !== 'undefined' &&
      window.innerWidth >= 1024
    ) {
      markDashboardTourSeen();
      const dashboard = matchPageTour('/dashboard');
      const t = window.setTimeout(() => {
        if (dashboard) runTour(dashboard.getSteps()); // let the dashboard render first
      }, 700);
      return () => window.clearTimeout(t);
    }
  }, [location.pathname, access.limited]);

  return (
    <div className="flex h-[calc(100vh-var(--impersonation-bar))] bg-background" data-shell="agent">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — w-64 expanded, w-16 icon rail when collapsed (desktop) */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-30 w-64 transform transition-all duration-200 ease-in-out',
          'lg:static lg:translate-x-0',
          collapsed ? 'lg:w-16' : 'lg:w-64',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <AgentSidebar collapsed={collapsed} onClose={() => setSidebarOpen(false)} access={access} />
      </aside>

      <main className="min-w-0 flex-1 overflow-auto bg-brand-surface">
        <AgentTopBar
          onMenuClick={() => setSidebarOpen(true)}
          onStartTour={startPageTour}
          hasTour={!!pageTour}
          tourLabel={pageTour?.label ?? 'Take a tour'}
          collapsed={collapsed}
          onToggleCollapse={toggleCollapsed}
        />
        <PaymentPromptBanner />
        <ClaimAccountBanner />
        <AiBudgetBanner />
        {access.limited && !accessState.pending && <LimitedSiteBar access={access} />}
        <LimitedOutlet state={accessState} />
      </main>
    </div>
  );
}
